/**
 * Calendar admin tool (PRD §5.6): authored events across sources, the source
 * registry, the daily CSV import, and legislative corrections — one page, four tabs. System-admin only
 * (route-gated). Every field is visible in the detail modal (AllFieldsSection
 * lesson): schema additions surface here on day one.
 */
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../../lib/api';
import Button from '@shared/ui/Button';
import AdminModal from '../../components/AdminModal';
import EntityAuditList from '../../components/EntityAuditList';
import ListPager from '../../components/ListPager';
import { useAdminList } from '../../hooks/useAdminList';
import SearchField from '../../../components/SearchField';
import {
  StandardTable,
  StandardTableHeader,
  StandardTableHeaderCell,
} from '../../../components/StandardTable';
import { EditIcon, DeleteIcon } from '../../../components/icons';
import ActionsHeaderHelp from '../../../components/ActionsHeaderHelp';
import { Download, Upload } from 'lucide-react';
import TimeComboBox from '../../../components/calendar/TimeComboBox';

type Tab = 'events' | 'sources' | 'import' | 'corrections';

interface AdminEvent {
  id: string;
  title: string;
  event_date: string;
  start_time: string | null;
  event_type: string | null;
  host_name: string | null;
  visibility: string;
  workspace_id: string | null;
  workspace_name: string | null;
  created_via: string;
  updated_via: string | null;
  updated_by: string | null;
  updated_at: string;
  source_name: string | null;
  external_id: string | null;
  [key: string]: unknown;
}

interface EventSource {
  id: string;
  name: string;
  tier: number;
  access_pattern: string;
  status: string;
  page_url: string | null;
  ics_url: string | null;
  license_basis: string | null;
  attribution_text: string | null;
  [key: string]: unknown;
}

const cell = 'px-4 py-2 text-sm text-text-primary dark:text-text-dark-primary';
const filterSelect =
  'rounded-md border border-border-light dark:border-border-dark-hairline bg-white dark:bg-background-dark-tertiary px-2 py-1.5 text-sm text-text-primary dark:text-text-dark-primary';

// Curation-skill vocabulary (import validator uses the same list)
const EVENT_TYPES = ['town_hall', 'party_meeting', 'convention_assembly', 'campaign_event',
  'volunteer_action', 'fundraiser', 'debate_forum', 'rally_protest', 'advocacy_briefing',
  'training', 'social', 'other'];

interface AdminEventsPayload { rows: AdminEvent[]; total: number; hasMore: boolean }
function parseAdminEvents(raw: unknown): AdminEventsPayload {
  const data = raw as { data?: AdminEvent[]; total?: number };
  const rows = data?.data ?? [];
  const total = data?.total ?? rows.length;
  return { rows, total, hasMore: rows.length > 0 && rows.length < total };
}

/** Provenance line per §5.6.3 — imported-then-modified is always visibly distinct */
function provenance(event: AdminEvent): string {
  const created = event.created_via === 'csv_import'
    ? `Imported${event.source_name ? ` from ${event.source_name}` : ''}`
    : `Created via ${event.created_via.replace('_', ' ')}`;
  const modified = event.updated_via && event.updated_via !== event.created_via
    ? ` · modified via ${event.updated_via.replace('_', ' ')} ${String(event.updated_at).slice(0, 10)}`
    : '';
  return created + modified;
}

const AdminCalendarPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as Tab) || 'events';
  const [reviewOnly, setReviewOnly] = useState(false);
  const [detailEvent, setDetailEvent] = useState<AdminEvent | null>(null);
  const [editEvent, setEditEvent] = useState<AdminEvent | null>(null);
  const [editForm, setEditForm] = useState<Record<string, string>>({});
  const [sourceModal, setSourceModal] = useState<Partial<EventSource> | null>(null);
  const [csvText, setCsvText] = useState('');
  const [correctionDate, setCorrectionDate] = useState('');
  const [correctionTarget, setCorrectionTarget] = useState<any | null>(null);
  const [correctionForm, setCorrectionForm] = useState<Record<string, string | boolean>>({});
  const [importReport, setImportReport] = useState<any>(null);
  const queryClient = useQueryClient();

  const [typeFilter, setTypeFilter] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('');
  const events = useAdminList<AdminEvent>({
    queryKey: 'calendar-events',
    endpoint: '/admin/calendar-events',
    parse: parseAdminEvents,
    limit: 50,
    initialSort: { key: 'event_date', direction: 'desc' },
    filters: {
      needs_review: reviewOnly || undefined,
      event_type: typeFilter || undefined,
      visibility: visibilityFilter || undefined,
    },
  });

  const sources = useQuery({
    queryKey: ['admin-calendar', 'sources'],
    queryFn: async () => {
      const response = await api.get('/admin/event-sources');
      return (response.data?.data ?? []) as EventSource[];
    },
    enabled: tab === 'sources',
  });

  const saveEvent = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = { ...editForm };
      if (typeof payload.topics === 'string') {
        payload.topics = String(payload.topics).split(';').map((t) => t.trim()).filter(Boolean);
      }
      return api.put(`/admin/calendar-events/${editEvent?.id}`, payload);
    },
    onSuccess: () => {
      toast.success('Event updated');
      setEditEvent(null);
      events.refetch();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Update failed'),
  });

  const deleteEvent = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/calendar-events/${id}`),
    onSuccess: () => {
      toast.success('Event deleted');
      events.refetch();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Delete failed'),
  });

  const saveSource = useMutation({
    mutationFn: async () => {
      const { id, ...payload } = sourceModal as EventSource;
      return id
        ? api.put(`/admin/event-sources/${id}`, payload)
        : api.post('/admin/event-sources', payload);
    },
    onSuccess: () => {
      toast.success('Source saved');
      setSourceModal(null);
      queryClient.invalidateQueries({ queryKey: ['admin-calendar', 'sources'] });
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Save failed'),
  });

  const legislativeDay = useQuery({
    queryKey: ['admin-calendar', 'leg-day', correctionDate],
    queryFn: async () => {
      const response = await api.get(`/calendar/day/${correctionDate}`);
      return (response.data?.data?.events ?? []) as any[];
    },
    enabled: tab === 'corrections' && /^\d{4}-\d{2}-\d{2}$/.test(correctionDate),
  });

  const openCorrection = async (event: any) => {
    let existing: Record<string, unknown> = {};
    try {
      const response = await api.get(`/admin/calendar-corrections/${event.calendar_id}`);
      existing = response.data?.data ?? {};
    } catch { /* 404 = no correction yet */ }
    setCorrectionTarget(event);
    setCorrectionForm({
      reading_title: String(existing.reading_title ?? ''),
      reading_date: String(existing.reading_date ?? '').slice(0, 10),
      reading_time: String(existing.reading_time ?? '').slice(0, 5),
      room_number: String(existing.room_number ?? ''),
      chamber: String(existing.chamber ?? ''),
      notes: String(existing.notes ?? ''),
      hidden: Boolean(existing.hidden),
    });
  };

  const saveCorrection = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(correctionForm)) {
        if (key === 'hidden') payload.hidden = value;
        else payload[key] = String(value).trim() || null;
      }
      return api.put(`/admin/calendar-corrections/${correctionTarget.calendar_id}`, payload);
    },
    onSuccess: () => {
      toast.success('Correction saved');
      setCorrectionTarget(null);
      queryClient.invalidateQueries({ queryKey: ['admin-calendar', 'leg-day'] });
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Save failed'),
  });

  const clearCorrection = useMutation({
    mutationFn: () => api.delete(`/admin/calendar-corrections/${correctionTarget.calendar_id}`),
    onSuccess: () => {
      toast.success('Correction cleared — scraped values restored');
      setCorrectionTarget(null);
      queryClient.invalidateQueries({ queryKey: ['admin-calendar', 'leg-day'] });
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Clear failed'),
  });

  const runImport = useMutation({
    mutationFn: () => api.post('/admin/calendar-events/import', { csv: csvText }),
    onSuccess: (response) => {
      setImportReport(response.data?.data ?? null);
      toast.success('Import complete');
      events.refetch();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Import failed'),
  });

  const openEdit = (event: AdminEvent) => {
    setEditEvent(event);
    setEditForm({
      title: event.title ?? '',
      event_date: String(event.event_date ?? '').slice(0, 10),
      start_time: event.start_time ? String(event.start_time).slice(0, 5) : '',
      event_type: event.event_type ?? '',
      host_name: event.host_name ?? '',
      visibility: event.visibility,
      topics: Array.isArray(event.topics) ? (event.topics as string[]).join(';') : '',
    });
  };

  const tabs: { key: Tab; label: string }[] = useMemo(
    () => [
      { key: 'events', label: 'Events' },
      { key: 'sources', label: 'Sources' },
      { key: 'import', label: 'Import' },
      { key: 'corrections', label: 'Corrections' },
    ],
    [],
  );

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-display font-semibold text-text-primary dark:text-text-dark-primary">
          Calendar
        </h1>
        <div className="flex gap-1.5">
          {tabs.map(({ key, label }) => (
            <Button
              key={key}
              variant={tab === key ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setSearchParams({ tab: key })}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      {tab === 'events' && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <SearchField
              value={events.search}
              onChange={events.setSearch}
              placeholder="Search title, host, source, city…"
              size="sm"
              className="w-72"
            />
            <select className={filterSelect} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} aria-label="Filter by type">
              <option value="">All types</option>
              {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className={filterSelect} value={visibilityFilter} onChange={(e) => setVisibilityFilter(e.target.value)} aria-label="Filter by visibility">
              <option value="">All visibility</option>
              {['public', 'shared', 'internal'].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-dark-secondary">
              <input type="checkbox" checked={reviewOnly} onChange={(e) => setReviewOnly(e.target.checked)} />
              Needs review only
            </label>
            <span className="ml-auto text-xs text-text-tertiary dark:text-text-dark-tertiary">
              {events.total} event{events.total === 1 ? '' : 's'}
            </span>
          </div>
          <StandardTable loading={events.isLoading} horizontalScroll>
            <StandardTableHeader>
              <tr>
                {([['event_date', 'Date'], ['title', 'Title'], ['event_type', 'Type'],
                  ['host_name', 'Host'], ['visibility', 'Visibility'], ['created_via', 'Provenance']] as const).map(([key, label]) => (
                  <StandardTableHeaderCell
                    key={key}
                    sortable
                    sortKey={key}
                    currentSortKey={events.sortKey}
                    sortDirection={events.sortDirection}
                    onSort={events.handleSort}
                  >
                    {label}
                  </StandardTableHeaderCell>
                ))}
                <StandardTableHeaderCell>
                  <span className="inline-flex items-center gap-1.5">
                    Actions
                    <ActionsHeaderHelp
                      icons={[
                        { key: 'edit', note: 'Admin events only — workspace events are edited in their workspace.' },
                        'delete',
                      ]}
                    />
                  </span>
                </StandardTableHeaderCell>
              </tr>
            </StandardTableHeader>
            <tbody>
              {events.rows.map((event) => (
                <tr key={event.id} className="border-b border-border-light dark:border-border-dark-hairline">
                  <td className={cell}>{String(event.event_date).slice(0, 10)}</td>
                  <td className={cell}>
                    <button type="button" className="text-primary-600 hover:underline" onClick={() => setDetailEvent(event)}>
                      {event.title}
                    </button>
                  </td>
                  <td className={cell}>{event.event_type ?? '—'}</td>
                  <td className={cell}>{event.workspace_name ? `[ws] ${event.workspace_name}` : event.host_name ?? '—'}</td>
                  <td className={cell}>{event.visibility}</td>
                  <td className={`${cell} text-xs text-text-tertiary dark:text-text-dark-tertiary`}>{provenance(event)}</td>
                  <td className={cell}>
                    <div className="flex gap-1">
                      {!event.workspace_id && (
                        <Button variant="ghost" size="sm" icon={EditIcon} ariaLabel="Edit event" title="Edit event" onClick={() => openEdit(event)} />
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={DeleteIcon}
                        ariaLabel="Delete event"
                        title="Delete event"
                        onClick={() => {
                          if (window.confirm(`Delete "${event.title}"?`)) deleteEvent.mutate(event.id);
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
              {!events.isLoading && events.rows.length === 0 && (
                <tr><td className={cell} colSpan={7}>No matching events{reviewOnly ? ' awaiting review' : ''}.</td></tr>
              )}
            </tbody>
          </StandardTable>
          <ListPager
            offset={events.offset}
            limit={events.limit}
            total={events.total}
            page={events.page}
            pageCount={events.pageCount}
            hasPrev={events.hasPrev}
            hasNext={events.hasNext}
            onPrev={events.prevPage}
            onNext={events.nextPage}
            noun="events"
          />
        </>
      )}

      {tab === 'sources' && (
        <>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" icon={Download} onClick={() => window.open('/api/admin/event-sources/export', '_blank')}>
              Download sources.md
            </Button>
            <Button variant="primary" size="sm" onClick={() => setSourceModal({ tier: 1, access_pattern: 'manual', status: 'pending' })}>
              Add Source
            </Button>
          </div>
          <StandardTable loading={sources.isLoading} horizontalScroll>
            <StandardTableHeader>
              <tr>
                <StandardTableHeaderCell>Name</StandardTableHeaderCell>
                <StandardTableHeaderCell>Tier</StandardTableHeaderCell>
                <StandardTableHeaderCell>Access</StandardTableHeaderCell>
                <StandardTableHeaderCell>Status</StandardTableHeaderCell>
                <StandardTableHeaderCell>Attribution</StandardTableHeaderCell>
                <StandardTableHeaderCell>
                  <span className="inline-flex items-center gap-1.5">
                    Actions
                    <ActionsHeaderHelp icons={['edit']} />
                  </span>
                </StandardTableHeaderCell>
              </tr>
            </StandardTableHeader>
            <tbody>
              {(sources.data ?? []).map((source) => (
                <tr key={source.id} className="border-b border-border-light dark:border-border-dark-hairline">
                  <td className={cell}>{source.name}</td>
                  <td className={cell}>{source.tier}</td>
                  <td className={cell}>{source.access_pattern}</td>
                  <td className={cell}>{source.status}</td>
                  <td className={cell}>{source.attribution_text ?? '—'}</td>
                  <td className={cell}>
                    <Button variant="ghost" size="sm" icon={EditIcon} ariaLabel="Edit source" title="Edit source" onClick={() => setSourceModal(source)} />
                  </td>
                </tr>
              ))}
              {!sources.isLoading && (sources.data ?? []).length === 0 && (
                <tr><td className={cell} colSpan={6}>No sources registered yet.</td></tr>
              )}
            </tbody>
          </StandardTable>
        </>
      )}

      {tab === 'import' && (
        <div className="space-y-3 max-w-3xl">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Paste or load the daily curation CSV. Rows upsert on <code>event_uid</code>; <code>needs_review</code> rows
            import hidden; <code>cancelled</code> rows delete; in-window rows missing from the file are demoted to
            hidden (&quot;unconfirmed&quot;), never deleted.
          </p>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () => setCsvText(String(reader.result ?? ''));
              reader.readAsText(file);
            }}
          />
          <textarea
            rows={8}
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            placeholder="event_uid,event_date,…"
            className="w-full rounded-md border border-border-light dark:border-border-dark-hairline bg-white dark:bg-background-dark-tertiary p-2 text-xs font-mono"
          />
          <Button variant="primary" icon={Upload} loading={runImport.isLoading} disabled={!csvText.trim()} onClick={() => runImport.mutate()}>
            Run Import
          </Button>
          {importReport && (
            <div className="rounded-lg border border-border-light dark:border-border-dark-hairline p-4 text-sm space-y-2">
              <div>
                Inserted {importReport.inserted} · Updated {importReport.updated} · Deleted {importReport.deleted} ·
                Held for review {importReport.review} · Unconfirmed (hidden) {importReport.unconfirmed}
              </div>
              {importReport.overwroteAdminEdit?.length > 0 && (
                <div>
                  <div className="font-semibold text-warning-700">Overwrote admin edits ({importReport.overwroteAdminEdit.length}):</div>
                  {importReport.overwroteAdminEdit.map((o: any) => (
                    <div key={o.uid} className="text-xs">
                      line {o.line} ({o.uid}): {JSON.stringify(o.before)} → {JSON.stringify(o.after)}
                    </div>
                  ))}
                </div>
              )}
              {importReport.rejected?.length > 0 && (
                <div>
                  <div className="font-semibold text-error-700">Rejected rows ({importReport.rejected.length}):</div>
                  {importReport.rejected.map((r: any) => (
                    <div key={r.line} className="text-xs">line {r.line}: {r.reasons.join('; ')}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'corrections' && (
        <div className="space-y-3">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Corrections overlay scraper-owned legislative events at read time and survive re-scrapes.
            Pick a date, then correct a row — blank fields keep the scraped value; Hidden removes the
            event from every user surface while the record stays inspectable here.
          </p>
          <input
            type="date"
            value={correctionDate}
            onChange={(e) => setCorrectionDate(e.target.value)}
            className="rounded-md border border-border-light dark:border-border-dark-hairline bg-white dark:bg-background-dark-tertiary px-2 py-1 text-sm"
          />
          {/* isInitialLoading, not isLoading: in React Query v4 a disabled query with no data (no date picked yet)
              reports isLoading=true forever, which left this table spinning. */}
          <StandardTable loading={legislativeDay.isInitialLoading} horizontalScroll>
            <StandardTableHeader>
              <tr>
                <StandardTableHeaderCell>Time</StandardTableHeaderCell>
                <StandardTableHeaderCell>Title</StandardTableHeaderCell>
                <StandardTableHeaderCell>Chamber</StandardTableHeaderCell>
                <StandardTableHeaderCell>Room</StandardTableHeaderCell>
                <StandardTableHeaderCell>State</StandardTableHeaderCell>
                <StandardTableHeaderCell>
                  <span className="inline-flex items-center gap-1.5">
                    Actions
                    <ActionsHeaderHelp icons={['edit']} />
                  </span>
                </StandardTableHeaderCell>
              </tr>
            </StandardTableHeader>
            <tbody>
              {(legislativeDay.data ?? []).map((event: any) => (
                <tr key={event.calendar_id} className="border-b border-border-light dark:border-border-dark-hairline">
                  <td className={cell}>{event.reading_time ?? '—'}</td>
                  <td className={cell}>{event.reading_title}</td>
                  <td className={cell}>{event.chamber ?? '—'}</td>
                  <td className={cell}>{event.room_number ?? '—'}</td>
                  <td className={cell}>{event.is_corrected ? 'corrected' : 'scraped'}</td>
                  <td className={cell}>
                    <Button variant="ghost" size="sm" icon={EditIcon} ariaLabel="Correct event" title="Correct event" onClick={() => openCorrection(event)} />
                  </td>
                </tr>
              ))}
              {!correctionDate && (
                <tr><td className={cell} colSpan={6}>Pick a date to list that day's legislative events.</td></tr>
              )}
              {legislativeDay.isError && (
                <tr><td className={cell} colSpan={6}>Could not load legislative events for {correctionDate}.</td></tr>
              )}
              {legislativeDay.isSuccess && (legislativeDay.data ?? []).length === 0 && (
                <tr><td className={cell} colSpan={6}>No legislative events on {correctionDate}.</td></tr>
              )}
            </tbody>
          </StandardTable>
        </div>
      )}

      {/* Correction editor — scraped values shown beside each field (§5.6.5) */}
      <AdminModal
        isOpen={correctionTarget !== null}
        title="Correct Legislative Event"
        subtitle={correctionTarget ? `#${correctionTarget.calendar_id} · ${correctionTarget.reading_title}` : undefined}
        onClose={() => setCorrectionTarget(null)}
        footer={
          <>
            <Button variant="danger" size="sm" loading={clearCorrection.isLoading} onClick={() => clearCorrection.mutate()}>
              Clear Correction
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setCorrectionTarget(null)}>Cancel</Button>
            <Button variant="primary" size="sm" loading={saveCorrection.isLoading} onClick={() => saveCorrection.mutate()}>Save</Button>
          </>
        }
      >
        {correctionTarget && (
          <div className="space-y-2">
            <label className="block text-sm" htmlFor="correction-time">
              <span className="text-xs font-semibold text-text-tertiary dark:text-text-dark-tertiary">
                reading_time{correctionTarget.reading_time ? ` — scraped: ${String(correctionTarget.reading_time).slice(0, 5)}` : ''}
              </span>
              <TimeComboBox
                id="correction-time"
                value={String(correctionForm.reading_time ?? '').slice(0, 5)}
                onChange={(v) => setCorrectionForm((f) => ({ ...f, reading_time: v }))}
              />
            </label>
            {([['reading_title', correctionTarget.reading_title], ['reading_date', String(correctionTarget.reading_date ?? '').slice(0, 10)],
              ['room_number', correctionTarget.room_number],
              ['chamber', correctionTarget.chamber], ['notes', null]] as const).map(([field, scraped]) => (
              <label key={field} className="block text-sm">
                <span className="text-xs font-semibold text-text-tertiary dark:text-text-dark-tertiary">
                  {field}{scraped ? ` — scraped: ${scraped}` : ''}
                </span>
                <input
                  className="input mt-0.5"
                  placeholder="blank = keep scraped value"
                  value={String(correctionForm[field] ?? '')}
                  onChange={(e) => setCorrectionForm((f) => ({ ...f, [field]: e.target.value }))}
                />
              </label>
            ))}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={Boolean(correctionForm.hidden)}
                onChange={(e) => setCorrectionForm((f) => ({ ...f, hidden: e.target.checked }))}
              />
              Hidden — remove this event from all user surfaces
            </label>
          </div>
        )}
      </AdminModal>

      {/* All-fields detail + audit trail (§5.6.2) */}
      <AdminModal
        isOpen={detailEvent !== null}
        title={detailEvent?.title ?? ''}
        subtitle={detailEvent ? provenance(detailEvent) : undefined}
        onClose={() => setDetailEvent(null)}
        size="lg"
      >
        {detailEvent && (
          <div className="space-y-4">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              {Object.entries(detailEvent).map(([key, value]) => (
                <React.Fragment key={key}>
                  <dt className="text-text-tertiary dark:text-text-dark-tertiary">{key}</dt>
                  <dd className="break-all">{value === null || value === undefined ? '—' : typeof value === 'object' ? JSON.stringify(value) : String(value)}</dd>
                </React.Fragment>
              ))}
            </dl>
            <EntityAuditList entityType="authored_event" entityId={detailEvent.id} />
          </div>
        )}
      </AdminModal>

      {/* Event edit */}
      <AdminModal
        isOpen={editEvent !== null}
        title="Edit Event"
        onClose={() => setEditEvent(null)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setEditEvent(null)}>Cancel</Button>
            <Button variant="primary" size="sm" loading={saveEvent.isLoading} onClick={() => saveEvent.mutate()}>Save</Button>
          </>
        }
      >
        <div className="space-y-2">
          {(['title', 'event_date', 'event_type', 'host_name', 'topics'] as const).map((field) => (
            <label key={field} className="block text-sm">
              <span className="text-xs font-semibold text-text-tertiary dark:text-text-dark-tertiary">{field}</span>
              <input
                className="input mt-0.5"
                value={editForm[field] ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, [field]: e.target.value }))}
              />
            </label>
          ))}
          <label className="block text-sm" htmlFor="admin-event-start">
            <span className="text-xs font-semibold text-text-tertiary dark:text-text-dark-tertiary">start_time</span>
            <TimeComboBox
              id="admin-event-start"
              value={(editForm.start_time ?? '').slice(0, 5)}
              onChange={(v) => setEditForm((f) => ({ ...f, start_time: v }))}
            />
          </label>
          <label className="block text-sm">
            <span className="text-xs font-semibold text-text-tertiary dark:text-text-dark-tertiary">visibility</span>
            <select
              className="input mt-0.5"
              value={editForm.visibility ?? 'public'}
              onChange={(e) => setEditForm((f) => ({ ...f, visibility: e.target.value }))}
            >
              <option value="internal">internal (hidden)</option>
              <option value="shared">shared</option>
              <option value="public">public</option>
            </select>
          </label>
        </div>
      </AdminModal>

      {/* Source add/edit */}
      <AdminModal
        isOpen={sourceModal !== null}
        title={sourceModal?.id ? 'Edit Source' : 'Add Source'}
        onClose={() => setSourceModal(null)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setSourceModal(null)}>Cancel</Button>
            <Button variant="primary" size="sm" loading={saveSource.isLoading} disabled={!sourceModal?.name} onClick={() => saveSource.mutate()}>Save</Button>
          </>
        }
      >
        {sourceModal && (
          <div className="space-y-2">
            {([['name', 'Name *'], ['organization', 'Organization'], ['party', 'Party code'], ['page_url', 'Page URL'],
              ['ics_url', 'ICS URL'], ['requested_by', 'Requested by (who asked, when, verified how)'],
              ['license_basis', 'License basis (required to automate a Tier-3 source)'],
              ['attribution_text', 'Attribution (e.g. Provided by Colorado Politics)'], ['attribution_url', 'Attribution URL'],
              ['notes', 'Notes']] as const).map(([field, label]) => (
              <label key={field} className="block text-sm">
                <span className="text-xs font-semibold text-text-tertiary dark:text-text-dark-tertiary">{label}</span>
                <input
                  className="input mt-0.5"
                  value={(sourceModal[field] as string) ?? ''}
                  onChange={(e) => setSourceModal((m) => ({ ...m, [field]: e.target.value }))}
                />
              </label>
            ))}
            <div className="grid grid-cols-3 gap-2">
              <label className="block text-sm">
                <span className="text-xs font-semibold text-text-tertiary dark:text-text-dark-tertiary">Tier</span>
                <select className="input mt-0.5"
                  value={sourceModal.tier ?? 1}
                  onChange={(e) => setSourceModal((m) => ({ ...m, tier: parseInt(e.target.value, 10) }))}>
                  {[1, 2, 3].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label className="block text-sm">
                <span className="text-xs font-semibold text-text-tertiary dark:text-text-dark-tertiary">Access</span>
                <select className="input mt-0.5"
                  value={sourceModal.access_pattern ?? 'manual'}
                  onChange={(e) => setSourceModal((m) => ({ ...m, access_pattern: e.target.value }))}>
                  {['ics', 'events_block', 'embed_browser', 'manual'].map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </label>
              <label className="block text-sm">
                <span className="text-xs font-semibold text-text-tertiary dark:text-text-dark-tertiary">Status</span>
                <select className="input mt-0.5"
                  value={sourceModal.status ?? 'pending'}
                  onChange={(e) => setSourceModal((m) => ({ ...m, status: e.target.value }))}>
                  {['pending', 'active', 'paused', 'blocked'].map((st) => <option key={st} value={st}>{st}</option>)}
                </select>
              </label>
            </div>
          </div>
        )}
      </AdminModal>
    </div>
  );
};

export default AdminCalendarPage;
